import type {GuidedItemsV2, GuidedV2SectionSource} from '../models/models';
import type {labelProviderInterface} from '../../LabelProviderInterface';
import {highlightText} from '../highlightText';
import {
  compareResultsByScore,
  onlyMatchingSource, parseHighlightedIntoJsx,
  shouldHighlightCalculator,
  sortAttributeByImportance,
  sortTagByImportance
} from '../utils';
import {
  GuidedGenericQuickLink, GuidedHistoryLinkDetails, GuidedHistoryOfSearchesItem,
  GuidedKeywordSuggestionItem,
  GuidedQuickLinkDetails,
  type GuidedV2Link, type GuidedV2LinkAttribute, type GuidedV2LinkTag
} from '../models/quick-link';


export type GuidedV2ResultListMapping = {
  searcher: string;
  sourceSections: string[];
};


export const highlightItems = (
  searchTerm: string,
  badgeHighlightThreshold: number,
  markupHighlightThreshold: number,
) => (item:GuidedV2Link<GuidedGenericQuickLink>) => {

    if(searchTerm.length < 3)
      return item;

    const shouldHighlightBadge = shouldHighlightCalculator(badgeHighlightThreshold, searchTerm);
    const shouldHighlightMarkup = shouldHighlightCalculator(markupHighlightThreshold, searchTerm);

    if(item.details.quickLinkTitle.includes('class="highlight"') ) {
      item.details.quickLinkMarkup = parseHighlightedIntoJsx(item.details.quickLinkTitle);
    }else{
      if(shouldHighlightMarkup(item.details.quickLinkTitle)){
        item.details.quickLinkMarkup = highlightText(item.details.quickLinkTitle, searchTerm);
      }
    }


    item.details.quickLinkBadges.tags
        = item.details.quickLinkBadges.tags.map(tag => {

          //according to https://jira.se.com/browse/SVS-29882 highlighting is not applicable to badges
          // leaving code commented out for future review
          // if(shouldHighlight(tag.tagText)){
          //   tag.tagMarkup = highlightText(tag.tagText, searchTerm);
          //   tag.importance = 'emphasized';
          // }
          return tag;
      }).sort(sortTagByImportance);


    item.details.quickLinkBadges.attributes
        = item.details.quickLinkBadges.attributes.map(attribute => {

          if(shouldHighlightBadge(attribute.attributeValue)){
            attribute.attributeValueMarkup = highlightText(attribute.attributeValue, searchTerm);
            // attribute.importance = 'emphasized'; //dont make blue pills even when there is match
          }

          return attribute;
      }).sort(sortAttributeByImportance);

    if (shouldHighlightMarkup(item.details.quickLinkTitle)) {
      item.details.quickLinkMarkup = highlightText(item.details.quickLinkTitle, searchTerm);
    }

    if (item.details.quickLinkDescription && shouldHighlightMarkup(item.details.quickLinkDescription)) {
      item.details.quickLinkDescriptionMarkup = highlightText(item.details.quickLinkDescription, searchTerm);
    }
    return item;
};

const singleGuidedItemMapper = (
  labelProvider: labelProviderInterface,
  unifiedCheckout: boolean
)=>(item:GuidedV2Link<GuidedGenericQuickLink>) =>{
    //TODO: ZOD! validate if the input element satisfies all required fields, otherwise reject it
    const title = item.details.quickLinkTitle;
    const url = item.details.quickLinkOpenUrl;
    const category = item.category;

    let description: string;
    if('quickLinkDescription' in item.details && typeof item.details.quickLinkDescription == 'string')
      description = item.details.quickLinkDescription;
    else
      description = undefined;

    let tags:GuidedV2LinkTag[];
    const details = item.details as Partial<GuidedGenericQuickLink>;
    if(
      'quickLinkBadges' in details
            && 'tags' in details.quickLinkBadges
            && details.quickLinkBadges.tags instanceof Array) {
      tags = (details.quickLinkBadges.tags as GuidedV2LinkTag[]).map(
        tag => {return {
          ...tag,
          tagText: labelProvider(
            `guided-v2.quick-link.badge.${tag.tagText}`, //TODO: labelProvider, uzupełnić prefiksy
            tag.tagText
          ),
        };});
    }else{
      tags = [];
    }

    let attributes:GuidedV2LinkAttribute[];
    if(
      'quickLinkBadges' in details
            && 'attributes' in details.quickLinkBadges
            && details.quickLinkBadges.attributes instanceof Array) {
      attributes = (details.quickLinkBadges.attributes as GuidedV2LinkAttribute[]).map(
        attribute => {return {
          ...attribute,
          attributeName: labelProvider(
            `guided-v2.quick-link.badge.attribute.${attribute.attributeType}.label`,
            attribute.attributeName
          ),
          attributeValue: labelProvider(
            `guided-v2.quick-link.badge.attribute.${attribute.attributeType}.${attribute.attributeValue}`,
            attribute.attributeValue
          ),
        };});
    }else{
      attributes = [];
    }


    return {
      template: item.template,
      score: item.score,
      details: {
        ...item.details,
        quickLinkTitle: title,
        quickLinkDescription: description,
        quickLinkOpenUrl: url,
        quickLinkBadges:{
          tags, attributes
        },
        quickLinkCategory: category,
        unifiedCheckout: unifiedCheckout,
      } satisfies GuidedGenericQuickLink,
    };

};

export function mapGuidedV2ItemsToGuidedV2ResultList<T extends GuidedQuickLinkDetails>(
  results: GuidedItemsV2<T>,
  sourceSections: GuidedV2SectionSource[],
  labelProvider: labelProviderInterface,
  maxItems: number,
  searchTerm: string,
  badgeHighlightThreshold: number,
  markupHighlightThreshold: number,
  unifiedCheckout: boolean
): GuidedV2Link<GuidedGenericQuickLink>[] {
//TODO: ZOD, we should validate if all items conform to GuidedV2Link<GuidedGenericQuickLink>, and reject if they dont
    return results
      .filter(onlyMatchingSource(sourceSections))
      .sort(compareResultsByScore)
      .slice(0, maxItems)
      .map(singleGuidedItemMapper(labelProvider, unifiedCheckout))
      .map(highlightItems(searchTerm, badgeHighlightThreshold, markupHighlightThreshold))
    ;
}


export const mapGuidedV2ItemToGuidedV2KeywordSuggestions = <T extends GuidedQuickLinkDetails> (
  item: GuidedV2Link<T>
): GuidedKeywordSuggestionItem => {
    //TODO: ZOD, we should validate if all items conform the required type, and reject if they dont
    return {
      keywordSuggestion: item.details.quickLinkTitle,
    } satisfies GuidedKeywordSuggestionItem;
};


export const mapGuidedV2ItemToHistoryItem = (
  item: GuidedV2Link<GuidedHistoryLinkDetails>,
  clickCallback: (keyword: string) => void,
  searchTerm: string,
): GuidedHistoryOfSearchesItem => {
    const deletionCallback = 'deletionCallback' in item.details ? item.details.deletionCallback : undefined;
    return {
      deletionCallback,
      clickCallback,
      title: highlightText(item.details.quickLinkTitle, searchTerm),
      openUrl: item.details.quickLinkOpenUrl,
      term: item.details.quickLinkTitle,
    };
};
